'use client';

import React, { useState, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { 
  XMarkIcon, 
  DocumentArrowUpIcon, 
  LinkIcon,
  ArrowUpTrayIcon 
} from '@heroicons/react/24/outline';

export interface ImportedContent {
  title: string;
  content: string;
  format: 'markdown' | 'html';
  filename?: string;
}

interface ImportArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (content: ImportedContent) => void;
}

export default function ImportArticleModal({ isOpen, onClose, onImport }: ImportArticleModalProps) {
  const [activeTab, setActiveTab] = useState<'file' | 'url'>('file');
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const extractTitleFromContent = (content: string, format: 'markdown' | 'html'): string => {
    if (format === 'markdown') {
      // Extract first H1 from markdown
      const h1Match = content.match(/^#\s+(.+)$/m);
      if (h1Match) return h1Match[1].trim();
    } else {
      // Extract first H1 from HTML
      const h1Match = content.match(/<h1[^>]*>(.+?)<\/h1>/i);
      if (h1Match) {
        // Strip HTML tags from title
        return h1Match[1].replace(/<[^>]+>/g, '').trim();
      }
    }
    return 'Untitled Article';
  };

  const detectFormat = (content: string, filename?: string): 'markdown' | 'html' => {
    // Check file extension first
    if (filename) {
      const ext = filename.toLowerCase().split('.').pop();
      if (ext === 'md' || ext === 'markdown') return 'markdown';
      if (ext === 'html' || ext === 'htm') return 'html';
    }
    
    // Detect by content
    if (content.trim().startsWith('<') || content.includes('</')) {
      return 'html';
    }
    return 'markdown';
  };

  const handleFileUpload = useCallback(async (file: File) => {
    setError(null);
    setIsLoading(true);

    try {
      const content = await file.text();
      const format = detectFormat(content, file.name);
      const title = extractTitleFromContent(content, format);

      onImport({
        title,
        content,
        format,
        filename: file.name
      });
      
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read file');
    } finally {
      setIsLoading(false);
    }
  }, [onImport, onClose]);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const file = files.find(f => 
      f.name.endsWith('.md') || 
      f.name.endsWith('.markdown') || 
      f.name.endsWith('.html') ||
      f.name.endsWith('.htm')
    );

    if (file) {
      handleFileUpload(file);
    } else {
      setError('Please drop a Markdown (.md) or HTML (.html) file');
    }
  }, [handleFileUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const handleUrlImport = useCallback(async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      let content: string;
      let contentType = '';
      
      // Try direct fetch first
      try {
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.statusText}`);
        }

        content = await response.text();
        contentType = response.headers.get('content-type') || '';
      } catch (directFetchError) {
        // If direct fetch fails (likely CORS), try with CORS proxy
        console.log('Direct fetch failed, trying CORS proxy...');
        
        // Use allOrigins.win as CORS proxy (free and reliable)
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
        
        const proxyResponse = await fetch(proxyUrl);
        
        if (!proxyResponse.ok) {
          throw new Error(`Failed to fetch via proxy: ${proxyResponse.statusText}`);
        }
        
        content = await proxyResponse.text();
        // Can't get content-type from proxy, will detect from content
      }
      
      // Detect format from content-type or URL
      let format: 'markdown' | 'html' = 'markdown';
      if (contentType.includes('text/html') || url.toLowerCase().endsWith('.html') || url.toLowerCase().endsWith('.htm')) {
        format = 'html';
      } else {
        // If no content-type, detect from content
        format = detectFormat(content, url.split('/').pop());
      }

      const title = extractTitleFromContent(content, format);
      const filename = url.split('/').pop() || 'imported-article';

      onImport({
        title,
        content,
        format,
        filename
      });
      
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch content from URL. The URL may be blocked by CORS policy or unreachable.');
    } finally {
      setIsLoading(false);
    }
  }, [url, onImport, onClose]);

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <DocumentArrowUpIcon className="h-6 w-6 text-blue-600 mr-3" />
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Import Article
              </Dialog.Title>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px px-6">
              <button
                onClick={() => setActiveTab('file')}
                className={`py-4 px-6 text-sm font-medium border-b-2 ${
                  activeTab === 'file'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ArrowUpTrayIcon className="h-5 w-5 inline mr-2" />
                Upload File
              </button>
              <button
                onClick={() => setActiveTab('url')}
                className={`py-4 px-6 text-sm font-medium border-b-2 ${
                  activeTab === 'url'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <LinkIcon className="h-5 w-5 inline mr-2" />
                Import from URL
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {activeTab === 'file' ? (
              <div>
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleFileDrop}
                  className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-4 text-sm text-gray-600">
                    Drag and drop your file here, or
                  </p>
                  <label className="mt-2 inline-block">
                    <span className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium">
                      browse files
                    </span>
                    <input
                      type="file"
                      accept=".md,.markdown,.html,.htm"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={isLoading}
                    />
                  </label>
                  <p className="mt-2 text-xs text-gray-500">
                    Supports: Markdown (.md) and HTML (.html) files
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="url-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Article URL
                </label>
                <input
                  id="url-input"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/article.md"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Enter a direct link to a Markdown or HTML file
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  💡 If the URL is blocked by CORS, we'll automatically try using a proxy
                </p>
                <button
                  onClick={handleUrlImport}
                  disabled={isLoading || !url.trim()}
                  className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Importing...' : 'Import from URL'}
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
